import { z } from 'zod';
import {
    TEAM_ASSOCIATION_HOST_TYPES,
    TEAM_ASSIGNMENT_MODES,
} from './team-association.registry.js';
import {
    DEFAULT_EXECUTION_MODE,
    ExecutionModeSchema,
    ExecutionTaskAssignmentSchema,
    MAX_OPERATIONAL_DEPENDENCIES,
    MAX_OPERATIONAL_DEPENDENCY_KEY_LENGTH,
    MAX_OPERATIONAL_MEMBERS,
    MAX_OPERATIONAL_RUN_ASSIGNMENTS,
    MAX_OPERATIONAL_RUN_TASKS,
    MAX_OPERATIONAL_TASKS,
    OperationalCustomFieldsSchema,
    OperationalDurationSchema,
    refineExecutionDefinition,
} from './operational-run.schema.js';

const HOST_TYPES = TEAM_ASSOCIATION_HOST_TYPES;

const optionalObjectId = z.preprocess((value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object') {
        const id = value._id ?? value.id;
        return id ? String(id) : null;
    }
    return String(value);
}, z.string().min(1).nullable().optional());

function pushUniqueUserId(ids, seen, value) {
    const id = value == null || value === ''
        ? ''
        : String(value._id || value.id || value).trim();
    if (!id || id === '[object Object]' || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
}

export function normalizeMemberUserIds(member = {}) {
    const seen = new Set();
    const ids = [];
    if (Array.isArray(member.userIds)) {
        for (const value of member.userIds) pushUniqueUserId(ids, seen, value);
    }
    pushUniqueUserId(ids, seen, member.userId);
    return ids;
}

export function normalizeAlternativeUserIds(member = {}) {
    const primary = new Set(normalizeMemberUserIds(member));
    const seen = new Set();
    const ids = [];
    if (Array.isArray(member.alternativeUserIds)) {
        for (const value of member.alternativeUserIds) pushUniqueUserId(ids, seen, value);
    }
    pushUniqueUserId(ids, seen, member.alternativeUserId);
    return ids.filter((id) => !primary.has(id));
}

export function primaryMemberUserId(member = {}) {
    return normalizeMemberUserIds(member)[0] || '';
}

export const TeamAssignmentMemberSchema = z.object({
    memberKey: z.string().trim().min(1).max(120),
    userId: z.string().min(1).optional(),
    userIds: z.array(z.string().min(1)).optional().default([]),
    role: z.string().trim().optional().default(''),
    responsibilities: z.string().trim().optional().default(''),
    alternativeUserId: optionalObjectId,
    alternativeUserIds: z.array(z.string().min(1)).optional().default([]),
    sequence: z.coerce.number().int().min(0).optional().nullable(),
}).transform((value, ctx) => {
    const userIds = normalizeMemberUserIds(value);
    if (!userIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['userIds'],
            message: 'At least one user is required',
        });
        return z.NEVER;
    }
    const alternativeUserIds = normalizeAlternativeUserIds({ ...value, userIds, userId: userIds[0] });
    return {
        ...value,
        userIds,
        userId: userIds[0],
        alternativeUserIds,
        alternativeUserId: alternativeUserIds[0] || null,
    };
});

const TeamAssignmentObjectSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    team: optionalObjectId,
    dutyDefinition: optionalObjectId,
    sourceKind: z.enum(['team', 'dutyDefinition']).optional(),
    parentType: z.enum(HOST_TYPES),
    parent: z.string().min(1),
    dutyProfile: optionalObjectId,
    assignmentMode: z.enum(TEAM_ASSIGNMENT_MODES),
    override: z.boolean().optional().default(false),
    members: z.array(TeamAssignmentMemberSchema).max(MAX_OPERATIONAL_MEMBERS).default([]),
    executionTasks: z.array(ExecutionTaskAssignmentSchema).max(MAX_OPERATIONAL_TASKS).default([]),
    executionMode: ExecutionModeSchema.optional().default(DEFAULT_EXECUTION_MODE),
    sourceProfile: optionalObjectId,
    clonedFromAssignment: optionalObjectId,
    sourceParentType: z.string().optional().nullable(),
    sourceParent: optionalObjectId,
    customFields: OperationalCustomFieldsSchema.optional(),
});

export const TeamAssignmentSchema = TeamAssignmentObjectSchema.superRefine(refineExecutionDefinition);

export const CreateTeamAssignmentSchema = z.object({
    parentType: z.enum(HOST_TYPES),
    parentId: z.string().min(1),
    dutyProfileId: z.string().min(1).optional().nullable(),
    copyFromAssignmentId: z.string().min(1).optional().nullable(),
});

export const AddTeamsBodySchema = z.object({
    teamIds: z.array(z.string().min(1)).max(MAX_OPERATIONAL_MEMBERS).optional().default([]),
    dutyDefinitionIds: z.array(z.string().min(1)).max(MAX_OPERATIONAL_RUN_ASSIGNMENTS).optional().default([]),
    dutyProfileId: z.string().min(1).optional().nullable(),
    copyFromAssignmentId: z.string().min(1).optional().nullable(),
    copyFromLinkedParents: z.boolean().optional().default(false),
});

/**
 * The R&R table patches timing/order without resending members, so both
 * collections are optional and cross-validated only when they arrive together.
 */
export const UpdateTeamAssignmentSchema = z.object({
    members: z.array(TeamAssignmentMemberSchema).max(MAX_OPERATIONAL_MEMBERS).optional(),
    executionTasks: z.array(ExecutionTaskAssignmentSchema).max(MAX_OPERATIONAL_TASKS).optional(),
    executionMode: ExecutionModeSchema.optional(),
    override: z.boolean().optional(),
}).superRefine((value, ctx) => {
    if (value.members && value.executionTasks) refineExecutionDefinition(value, ctx);
});

export const UpdateParentExecutionPlanSchema = z.object({
    tasks: z.array(z.object({
        assignmentId: z.string().min(1),
        taskKey: z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
        sequence: z.coerce.number().int().min(0).optional(),
        dependencyTaskKeys: z.array(
            z.string().trim().min(1).max(MAX_OPERATIONAL_DEPENDENCY_KEY_LENGTH),
        ).max(MAX_OPERATIONAL_DEPENDENCIES).optional(),
        expectedDuration: OperationalDurationSchema.optional().nullable(),
        targetCompletion: OperationalDurationSchema.optional().nullable(),
        warningBefore: OperationalDurationSchema.optional().nullable(),
    })).max(MAX_OPERATIONAL_RUN_TASKS),
});
